import { useMemo } from "react";
import { useCompanyDetails } from "./useCompanyDetails";
import { YEAR_MONTH_DAY_FORMAT } from "../common/constant";
import { convertLocalToCET, isLastDayOfMonth } from "../common/date";
import moment from "moment";

export const useBookingPeriod = () => {

    const bookingPeriodStartDate = moment().format(YEAR_MONTH_DAY_FORMAT);
    const {companyDetails} = useCompanyDetails();

    const bookingPeriodDurationInMonths = useMemo(() => {
        let bookingPeriod;
        if (companyDetails?.bookingPeriod && companyDetails?.bookingOpeningTime) {
            /**
             * The booking period window is set as a number of months from the current month.
             * Also, there is a time (in CET) of the first day of a new month when the booking period opens.
             * There are some cases where the booking period must be adjusted by 1, either increased or decreased.
             * Adjusting the window depends on the local timezone of the client compared to the CET booking opening day and time.
             * These cases are:
             * 1. Local timezone is on the first day of a new month and CET is on the last day of the previous month or on the first day of a new month but before opening time -> booking period must be decreased by 1
             * 2. Local timezone is after the first day of new month and CET is on the first day of new month but before opening time                                           -> booking period must be decreased by 1
             * 3. Local timezone is still on the last day of the previous month and CET is on the first day of a new month and after booking opening time                       -> booking period must be increased by 1
             * For all the other cases, the booking period is the default.
             */

            bookingPeriod = companyDetails.bookingPeriod;
            const nowLocal = new Date();
            const dayOfMonthLocal = nowLocal.getDate();
            const nowCET = convertLocalToCET(nowLocal);
            const hoursCET = nowCET.getHours();
            const dayOfMonthCET = nowCET.getDate();
            const isBeforeBookingOpeningTime = hoursCET < moment(companyDetails.bookingOpeningTime, "hh:mm A").hours();

            if (dayOfMonthLocal === 1) { // case 1
                if (isLastDayOfMonth(nowCET) || (dayOfMonthCET === 1 && isBeforeBookingOpeningTime)) {
                    bookingPeriod -= 1;
                }
            } else {
                if (isLastDayOfMonth(nowLocal)) { // case 3
                    if (dayOfMonthCET === 1 && !isBeforeBookingOpeningTime) {
                        bookingPeriod += 1;
                    }
                } else if (dayOfMonthCET === 1 && isBeforeBookingOpeningTime) { // case 2
                    bookingPeriod -= 1;
                }
            }
        }
        return bookingPeriod;
    }, [companyDetails?.bookingPeriod, companyDetails?.bookingOpeningTime]);

    const bookingPeriodEndDate = useMemo(() => {
        let endDate;
        if (bookingPeriodDurationInMonths) {
            endDate = moment(bookingPeriodStartDate).add(bookingPeriodDurationInMonths, "months")?.endOf("month")?.format(YEAR_MONTH_DAY_FORMAT);
        }
        return endDate;
    }, [bookingPeriodDurationInMonths]);

    return { bookingPeriodStartDate, bookingPeriodEndDate, bookingPeriodDurationInMonths };
};
